/**
 * Staged copy for the Placement Review loading state.
 *
 * The request is slow in a way a spinner cannot express. Two phases run before
 * anything renders: a GAQL read of every PMax/Display/Demand Gen placement, then
 * LLM classification in sequential batches of 40. On a small account that is a
 * few seconds. On a large one it is not:
 *
 *   smithandson.net        3.4s read,      0 placements  → instant
 *   mikehammermoving.com  85.5s read, 92,977 placements  → ~2,325 batches
 *                                                          at ~14s ≈ 9 hours
 *
 * So the copy escalates, and then stops joking. The final stage is a warning,
 * because past a few minutes the honest thing to tell an operator is that this
 * account probably will not finish — not to keep waiting.
 *
 * No stage may imply progress. The backend streams none, so a percentage or an
 * ETA here would be invented.
 */

export type PlacementLoadingTone = 'normal' | 'warning';

export interface PlacementLoadingStage {
  headline: string;
  sub: string;
  tone: PlacementLoadingTone;
}

/** Past this, completion is unlikely enough that we say so plainly. */
export const PLACEMENT_LOADING_WARN_AFTER_SECONDS = 300;

interface Step {
  after: number;
  headline: string;
  sub: string;
  tone?: PlacementLoadingTone;
}

// Ordered by `after` descending at lookup time; first match wins.
const STEPS: Step[] = [
  {
    after: 0,
    headline: 'Asking Google for your placements…',
    sub: 'This usually takes a few seconds.',
  },
  {
    after: 10,
    headline: "Google's API is thinking about it.",
    sub: 'Bigger accounts have more placements to hand over. Nothing is broken.',
  },
  {
    after: 30,
    headline: 'Still Google. Still not us.',
    sub: "We are just sitting here refreshing too. Please wait, and don't leave us.",
  },
  {
    after: 75,
    headline: 'Google is taking its sweet time.',
    sub: 'Every placement gets read, then classified in batches. There can be a lot of them.',
  },
  {
    after: 150,
    headline: 'Outnumbered, but holding.',
    sub: 'Still running. Nothing has crashed. We would tell you.',
  },
  {
    after: PLACEMENT_LOADING_WARN_AFTER_SECONDS,
    headline: 'This account may be too large to review in one pass.',
    sub:
      'Placements are classified sequentially in batches of 40, so an account with ' +
      'tens of thousands can exceed the request timeout before it ever returns. ' +
      'Try a smaller account, or ask engineering to cap the run.',
    tone: 'warning',
  },
];

export function placementLoadingStage(elapsedSeconds: number): PlacementLoadingStage {
  const secs = Number.isFinite(elapsedSeconds) ? Math.max(0, elapsedSeconds) : 0;
  let match = STEPS[0];
  for (const step of STEPS) {
    if (secs >= step.after) match = step;
  }
  return { headline: match.headline, sub: match.sub, tone: match.tone ?? 'normal' };
}

/** "1:23" — plain elapsed clock, so the operator can see it is still moving. */
export function formatElapsed(totalSeconds: number): string {
  const secs = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
